import { Fragment, type ReactNode } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import clsx from 'clsx';

import Button from '../Button/Button';
import Icon from '../Icon/Icon';

import { type HeaderNavGroup } from './Header';

import styles from './Header.module.css';

interface HamburgerMenuProps {
  open: boolean;
  onClose: () => void;
  menuItems?: HeaderNavGroup[];
  isAuthenticated?: boolean;
  authenticatedCta?: ReactNode;
  theme?: 'light' | 'dark';
  loginLabel?: string;
  signupLabel?: string;
  logoutLabel?: string;
  onLogin?: () => void;
  onSignup?: () => void;
  onLogout?: () => void;
  actionsSuffix?: ReactNode;
}

const isCurrentPageLink = (href: string) => {
  if (typeof window === 'undefined') return false;

  try {
    const target = new URL(href, window.location.href);
    const normalizePath = (path: string) => path.replace(/\/(index\/?)?$/, '') || '/';

    return (
      target.origin === window.location.origin &&
      normalizePath(target.pathname) === normalizePath(window.location.pathname)
    );
  } catch {
    return false;
  }
};

export default function HamburgerMenu({
  open,
  onClose,
  menuItems = [],
  isAuthenticated = false,
  authenticatedCta,
  theme = 'light',
  loginLabel = 'Login',
  signupLabel = 'Sign up',
  logoutLabel = 'Log out',
  onLogin,
  onSignup,
  onLogout,
  actionsSuffix,
}: HamburgerMenuProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={clsx(styles.dialog, styles[theme])}
    >
      <DialogBackdrop
        transition
        className={styles.dialogBackdrop}
      />
      <DialogTitle className={styles.visuallyHidden}>Navigation menu</DialogTitle>
      <DialogPanel
        transition
        className={styles.dialogPanel}
      >
        {menuItems.length > 0 && (
          <nav
            className={styles.menu}
            aria-label="Menu"
          >
            {menuItems.map(({ label, link, item, _editingUrl }, index) => (
              <Fragment key={label ?? index}>
                {index > 0 && <hr className={styles.divider} />}
                <div
                  className={clsx(styles.menuGroup, link && styles.menuWithLink)}
                  data-datocms-content-link-url={_editingUrl}
                >
                  {label &&
                    (link ? (
                      <a
                        className={clsx(item && styles.menuGroupLabel, styles.menuGroupLink)}
                        href={link}
                        aria-current={isCurrentPageLink(link) || undefined}
                      >
                        <Icon name="home" />
                        {label}
                      </a>
                    ) : (
                      <p className={styles.menuGroupLabel}>{label}</p>
                    ))}
                  {item}
                </div>
              </Fragment>
            ))}
          </nav>
        )}

        <hr className={styles.divider} />
        <div className={styles.dialogActions}>
          {isAuthenticated ? (
            <>
              {authenticatedCta}
              <Button
                variant="ghost"
                size="sm"
                onClick={onLogout}
              >
                {logoutLabel}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="primary"
                size="sm"
                onClick={onSignup}
              >
                {signupLabel}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onLogin}
              >
                {loginLabel}
              </Button>
            </>
          )}
          {actionsSuffix}
        </div>
      </DialogPanel>
    </Dialog>
  );
}
